<template>
  <div data-tour="invoices-list" ref="rootEl">
    <!-- Loading -->
    <div v-if="isLoading" class="card text-center py-12">
      <div class="animate-pulse space-y-3">
        <div class="h-4 w-40 bg-stroke dark:bg-stroke-dark rounded mx-auto"></div>
        <div class="h-3 w-64 bg-stroke dark:bg-stroke-dark rounded mx-auto"></div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="card border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
      <div class="flex items-center">
        <ExclamationTriangleIcon class="h-5 w-5 text-red-500 dark:text-red-400 mr-3 flex-shrink-0" />
        <div>
          <p class="font-medium text-red-800 dark:text-red-300">Failed to load stats</p>
          <p class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>
        </div>
        <button @click="load" class="ml-auto btn-secondary text-sm">Retry</button>
      </div>
    </div>

    <!-- No rate configured — nothing payroll-related to show -->
    <div v-else-if="weeks.length === 0" class="card text-center py-8 text-sm text-fg-muted dark:text-fg-muted-dark">
      No payroll data available.
    </div>

    <!-- Table (pane ≥680px) / Cards (pane <680px — tables are unreadable once the pane gets narrow).
         This breakpoint is measured off the pane's own width via ResizeObserver, not the viewport
         (Tailwind's md: breakpoint), since this tab lives in a user-resizable pane that can go
         narrow on a wide desktop window. -->
    <template v-else>
    <div v-if="!props.contained" class="card mb-3">
      <template v-if="summary.nextDay">
        <div class="text-xs text-fg-muted dark:text-fg-muted-dark uppercase tracking-wide">Next payout</div>
        <div class="flex items-baseline justify-between gap-3 mt-0.5">
          <div class="text-2xl font-bold text-fg-base dark:text-fg-base-dark">{{ fmt(summary.nextAmount) }}</div>
          <div class="text-sm font-medium text-blue-700 dark:text-blue-400 whitespace-nowrap">Paying {{ dayLabel(summary.nextDay) }}</div>
        </div>
        <div class="text-xs text-fg-faint dark:text-fg-faint-dark">{{ summary.nextWeeks.map(rangeLabel).join(', ') }}</div>
      </template>

      <dl class="text-sm space-y-1" :class="summary.nextDay ? 'mt-3 pt-3 border-t border-stroke dark:border-stroke-dark' : ''">
        <div v-if="summary.current" class="flex justify-between gap-3">
          <dt class="text-fg-muted dark:text-fg-muted-dark">This week so far</dt>
          <dd class="font-medium text-fg-secondary dark:text-fg-secondary-dark">{{ fmt(summary.current.total_due) }} · {{ plural(summary.current.lesson_count, 'lesson') }}</dd>
        </div>
        <div v-if="ytdPaid" class="flex justify-between gap-3">
          <dt class="text-fg-muted dark:text-fg-muted-dark">Paid in {{ year }}</dt>
          <dd class="font-medium text-fg-secondary dark:text-fg-secondary-dark">{{ fmt(ytdPaid) }}</dd>
        </div>
        <div v-if="summary.lastPaid" class="flex justify-between gap-3">
          <dt class="text-fg-muted dark:text-fg-muted-dark">Last paid</dt>
          <dd class="font-medium text-fg-secondary dark:text-fg-secondary-dark">{{ fmt(summary.lastPaid.paid_amount) }} · {{ fmtDate(summary.lastPaid.paid_at) }}</dd>
        </div>
      </dl>

      <div v-if="summary.toConfirm.length || summary.unmarked" class="mt-3 flex flex-wrap gap-2">
        <button
          v-for="w in summary.toConfirm" :key="w.week_start"
          class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          :disabled="confirming.has(w.week_start)"
          @click="confirmInvoice(w)"
        >
          <CheckCircleIcon class="h-3.5 w-3.5" />
          {{ confirming.has(w.week_start) ? 'Confirming…' : `Confirm ${rangeLabel(w)} · ${fmt(w.total_due)}` }}
        </button>
        <button
          v-if="summary.unmarked"
          class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors bg-amber-500 text-white hover:bg-amber-600"
          @click="goMarkAttendance"
        >
          <ClipboardDocumentCheckIcon class="h-3.5 w-3.5" />
          Mark attendance ({{ summary.unmarked }})
        </button>
      </div>
    </div>

    <div v-if="!isNarrow" class="bg-surface-raised dark:bg-surface-dark rounded-lg border border-stroke dark:border-stroke-dark overflow-hidden">
      <div class="overflow-auto" :style="{ maxHeight: props.maxHeight }">
        <table class="min-w-full divide-y divide-stroke dark:divide-stroke-dark">
          <thead class="sticky top-0 z-10 bg-surface-muted dark:bg-surface-raised-dark">
            <tr>
              <th class="px-4 py-2.5 text-left  text-xs font-medium uppercase tracking-wider text-fg-muted dark:text-fg-muted-dark whitespace-nowrap">Week</th>
              <th class="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-fg-muted dark:text-fg-muted-dark whitespace-nowrap">Hours</th>
              <th class="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-fg-muted dark:text-fg-muted-dark whitespace-nowrap">Regular Pay</th>
              <th v-if="showGroupPay" class="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-fg-muted dark:text-fg-muted-dark whitespace-nowrap">Group Pay</th>
              <th class="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-fg-muted dark:text-fg-muted-dark whitespace-nowrap">Missing</th>
              <th class="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-fg-muted dark:text-fg-muted-dark whitespace-nowrap">Total Due</th>
              <th class="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-fg-muted dark:text-fg-muted-dark whitespace-nowrap">Paid</th>
              <th class="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-fg-muted dark:text-fg-muted-dark whitespace-nowrap">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-stroke dark:divide-stroke-dark">
            <template v-for="w in weeks" :key="w.week_start">
              <tr
                class="cursor-pointer transition-colors"
                :class="rowClass(w)"
                @click="toggleExpand(w.week_start)"
              >
                <!-- Week -->
                <td class="px-4 py-3 text-sm whitespace-nowrap">
                  <div class="flex items-center gap-1.5 font-medium text-fg-base dark:text-fg-base-dark">
                    <ChevronRightIcon class="h-3.5 w-3.5 text-fg-faint dark:text-fg-faint-dark transition-transform" :class="expanded.has(w.week_start) ? 'rotate-90' : ''" />
                    {{ rangeLabel(w) }}
                  </div>
                </td>

                <!-- Hours -->
                <td class="px-4 py-3 text-sm text-right text-fg-secondary dark:text-fg-secondary-dark">
                  <div class="font-medium">{{ totalHours(w) }}h</div>
                  <div v-for="(cat, catName) in w.category_breakdown" :key="catName" class="text-xs text-fg-faint dark:text-fg-faint-dark whitespace-nowrap">
                    incl. {{ cat.hours }}h {{ catName }}
                  </div>
                </td>

                <!-- Regular Pay -->
                <td class="px-4 py-3 text-sm text-right text-fg-secondary dark:text-fg-secondary-dark">
                  <div>{{ fmt(w.regular_pay) }}</div>
                  <div v-for="(cat, catName) in w.category_breakdown" :key="catName" class="text-xs text-fg-faint dark:text-fg-faint-dark whitespace-nowrap">
                    + {{ fmt(cat.pay) }} ({{ catName }})
                  </div>
                </td>

                <!-- Group Pay -->
                <td v-if="showGroupPay" class="px-4 py-3 text-sm text-right text-fg-secondary dark:text-fg-secondary-dark whitespace-nowrap">
                  <template v-if="w.group_lesson_count > 0">
                    <div>{{ fmt(w.group_pay) }}</div>
                    <div class="text-xs text-fg-faint dark:text-fg-faint-dark">{{ w.group_lesson_count }} lesson{{ w.group_lesson_count !== 1 ? 's' : '' }}</div>
                  </template>
                  <span v-else class="text-stroke-strong dark:text-stroke-strong-dark">—</span>
                </td>

                <!-- Missing -->
                <td class="px-4 py-3 text-sm text-right whitespace-nowrap">
                  <span
                    v-if="w.not_reported_lessons === 0"
                    class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                  >
                    <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-xs font-semibold"
                  >
                    {{ w.not_reported_lessons }}
                  </span>
                </td>

                <!-- Total Due -->
                <td class="px-4 py-3 text-sm text-right font-semibold text-fg-base dark:text-fg-base-dark whitespace-nowrap">
                  {{ fmt(w.total_due) }}
                </td>

                <!-- Paid -->
                <td class="px-4 py-3 text-sm text-right whitespace-nowrap">
                  <template v-if="w.payment_status === 'paid'">
                    <div class="font-medium text-green-700 dark:text-green-400">{{ fmt(w.paid_amount) }}</div>
                    <div class="text-xs text-fg-faint dark:text-fg-faint-dark">{{ fmtDate(w.paid_at) }}</div>
                  </template>
                  <span v-else class="text-stroke-strong dark:text-stroke-strong-dark">—</span>
                </td>

                <!-- Status (read-only — no admin actions) -->
                <td class="px-4 py-3 text-right whitespace-nowrap">
                  <div :class="['text-xs font-medium', statusInfo(w).textClass]">{{ statusInfo(w).text }}</div>
                  <div v-if="statusInfo(w).sub" :class="['text-xs', statusInfo(w).subClass || 'text-fg-faint dark:text-fg-faint-dark']">{{ statusInfo(w).sub }}</div>
                </td>
              </tr>

              <!-- Expanded lesson detail -->
              <tr v-if="expanded.has(w.week_start)">
                <td :colspan="showGroupPay ? 8 : 7" class="px-4 py-3 bg-surface-muted dark:bg-surface-raised-dark">
                  <dl v-if="w.lines.length" class="text-xs space-y-1 mb-3 max-w-sm">
                    <div v-for="(line, i) in w.lines" :key="i" class="flex justify-between gap-3">
                      <dt class="text-fg-muted dark:text-fg-muted-dark">{{ line.label }}</dt>
                      <dd :class="line.amount < 0 ? 'text-amber-600 dark:text-amber-400' : 'text-fg-secondary dark:text-fg-secondary-dark'">{{ fmtSigned(line.amount) }}</dd>
                    </div>
                    <div class="flex justify-between gap-3 pt-1 border-t border-stroke dark:border-stroke-dark font-semibold text-fg-base dark:text-fg-base-dark">
                      <dt>Total</dt><dd>{{ fmt(w.total_due) }}</dd>
                    </div>
                  </dl>
                  <div v-if="lessonsLoading[w.week_start]" class="text-xs text-fg-muted dark:text-fg-muted-dark py-2">Loading lessons…</div>
                  <div v-else-if="!lessonsByWeek[w.week_start]?.length" class="text-xs text-fg-muted dark:text-fg-muted-dark py-2">No lessons recorded this week.</div>
                  <table v-else class="w-full text-sm border-separate border-spacing-0">
                    <thead>
                      <tr class="text-xs text-fg-muted dark:text-fg-muted-dark">
                        <th class="text-left font-medium pb-1 pr-2">Date</th>
                        <th class="text-left font-medium pb-1 pr-2">Time</th>
                        <th class="text-left font-medium pb-1 pr-2">Lesson</th>
                        <th class="text-left font-medium pb-1">Attendance</th>
                        <th class="text-right font-medium pb-1">Pay</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-stroke-subtle dark:divide-surface-dark">
                      <tr v-for="(l, li) in lessonsByWeek[w.week_start]" :key="li">
                        <td class="py-1 pr-2 text-fg-secondary dark:text-fg-secondary-dark whitespace-nowrap">{{ fmtLessonDate(l.lesson_date) }}</td>
                        <td class="py-1 pr-2 text-fg-secondary dark:text-fg-secondary-dark whitespace-nowrap">{{ l.lesson_time || '—' }}</td>
                        <td class="py-1 pr-2 text-fg-secondary dark:text-fg-secondary-dark">{{ l.lesson_title }}</td>
                        <td class="py-1">
                          <span
                            v-for="(a, ai) in l.attendance"
                            :key="ai"
                            class="inline-flex items-center gap-1 mr-3 whitespace-nowrap text-fg-secondary dark:text-fg-secondary-dark"
                          >
                            <span class="inline-block w-2 h-2 rounded-full flex-shrink-0" :class="statusDot(a.status)"></span>
                            {{ a.name }} <span class="text-fg-faint dark:text-fg-faint-dark">({{ statusLabel(a.status) }})</span>
                          </span>
                        </td>
                        <td class="py-1 text-right whitespace-nowrap" :class="l.pay == null ? 'text-fg-faint dark:text-fg-faint-dark' : 'text-fg-secondary dark:text-fg-secondary-dark'">
                          {{ l.pay == null ? 'unpaid' : fmt(l.pay) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <button v-if="canLoadMore" class="w-full py-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50" :disabled="loadingMore" @click="loadMore">
          {{ loadingMore ? 'Loading…' : 'Show older weeks' }}
        </button>
      </div>
    </div>

    <!-- Cards (pane <680px). Height-constrained + internally scrollable only in `contained`
         usage (the resizable desktop pane, which clips overflow) — on the plain mobile page
         it renders at natural height so the page itself scrolls, same as before this tab
         gained its own internal scrolling. -->
    <div v-else class="space-y-2" :class="props.contained ? 'overflow-auto' : ''" :style="props.contained ? { maxHeight: props.maxHeight } : {}">
      <template v-for="m in months" :key="m.key">
        <div class="flex items-baseline justify-between px-1 pt-2 text-xs font-medium uppercase tracking-wide text-fg-muted dark:text-fg-muted-dark">
          <span>{{ m.label }}</span>
          <span>{{ fmt(m.total) }}</span>
        </div>
        <div
          v-for="w in m.weeks"
          :key="w.week_start"
          class="card cursor-pointer"
          :class="cardBorderClass(w)"
          @click="toggleExpand(w.week_start)"
        >
          <!-- Amount is the headline; status (with its date) and hours/lessons underneath. -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-center gap-1.5 min-w-0 font-medium text-fg-base dark:text-fg-base-dark">
              <ChevronRightIcon class="h-3.5 w-3.5 text-fg-faint dark:text-fg-faint-dark transition-transform flex-shrink-0" :class="expanded.has(w.week_start) ? 'rotate-90' : ''" />
              <span class="truncate">{{ rangeLabel(w) }}</span>
            </div>
            <div class="text-lg font-bold leading-tight text-fg-base dark:text-fg-base-dark whitespace-nowrap">{{ fmt(w.total_due) }}</div>
          </div>
          <div class="mt-1.5 flex items-center justify-between gap-3 text-xs">
            <div class="flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
              <span :class="['badge', statusInfo(w).pillClass]">{{ statusInfo(w).text }}</span>
              <span v-if="statusInfo(w).sub" :class="statusInfo(w).subClass || 'text-fg-faint dark:text-fg-faint-dark'">{{ statusInfo(w).sub }}</span>
            </div>
            <span v-if="w.lesson_count" class="text-fg-faint dark:text-fg-faint-dark whitespace-nowrap">{{ totalHours(w) }}h · {{ plural(w.lesson_count, 'lesson') }}</span>
          </div>

          <!-- The week's blocker or next step, as a button -->
          <div v-if="statusInfo(w).action || confirmError[w.week_start]" class="mt-3 text-right">
            <button
              v-if="statusInfo(w).action === 'attendance'"
              class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors
                     bg-amber-500 text-white hover:bg-amber-600"
              @click.stop="goMarkAttendance"
            >
              <ClipboardDocumentCheckIcon class="h-3.5 w-3.5" />
              Mark attendance ({{ w.not_reported_lessons }})
            </button>
            <button
              v-if="statusInfo(w).action === 'confirm'"
              class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors
                     bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              :disabled="confirming.has(w.week_start)"
              @click.stop="confirmInvoice(w)"
            >
              <CheckCircleIcon class="h-3.5 w-3.5" />
              {{ confirming.has(w.week_start) ? 'Confirming…' : 'Confirm Invoice' }}
            </button>
            <p v-if="confirmError[w.week_start]" class="mt-1 text-xs text-red-600 dark:text-red-400">
              {{ confirmError[w.week_start] }}
            </p>
          </div>

          <!-- Expanded: how the total adds up, then each lesson and what it paid -->
          <div v-if="expanded.has(w.week_start)" class="mt-3 pt-3 border-t border-stroke dark:border-stroke-dark text-xs" @click.stop>
            <dl v-if="w.lines.length" class="space-y-1 mb-3">
              <div v-for="(line, i) in w.lines" :key="i" class="flex justify-between gap-3">
                <dt class="text-fg-muted dark:text-fg-muted-dark">{{ line.label }}</dt>
                <dd :class="line.amount < 0 ? 'text-amber-600 dark:text-amber-400' : 'text-fg-secondary dark:text-fg-secondary-dark'">{{ fmtSigned(line.amount) }}</dd>
              </div>
              <div class="flex justify-between gap-3 pt-1 border-t border-stroke dark:border-stroke-dark font-semibold text-fg-base dark:text-fg-base-dark">
                <dt>Total</dt><dd>{{ fmt(w.total_due) }}</dd>
              </div>
              <div v-if="w.payment_status === 'paid' && (paymentDiff(w) !== 0 || w.settled_elsewhere)" class="flex justify-between gap-3 text-green-700 dark:text-green-400">
                <dt>Paid {{ fmtDate(w.paid_at) }}</dt><dd>{{ fmt(w.paid_amount) }}</dd>
              </div>
              <div v-if="w.payment_status === 'paid' && w.settled_elsewhere" class="flex justify-between gap-3 text-green-700 dark:text-green-400">
                <dt>{{ settledLabel(w) }}</dt><dd>{{ fmt(w.settled_elsewhere.amount) }}</dd>
              </div>
            </dl>

            <div v-if="lessonsLoading[w.week_start]" class="text-fg-muted dark:text-fg-muted-dark py-1">Loading lessons…</div>
            <div v-else-if="!lessonsByWeek[w.week_start]?.length" class="text-fg-muted dark:text-fg-muted-dark py-1">No lessons recorded this week.</div>
            <div v-else class="space-y-2.5">
              <div v-for="(l, li) in lessonsByWeek[w.week_start]" :key="li">
                <div class="flex items-center justify-between gap-3 font-medium text-fg-secondary dark:text-fg-secondary-dark">
                  <span>{{ fmtLessonDate(l.lesson_date) }} · {{ l.lesson_time || '—' }}</span>
                  <span :class="l.pay == null ? 'text-fg-faint dark:text-fg-faint-dark font-normal' : ''">{{ l.pay == null ? 'unpaid' : fmt(l.pay) }}</span>
                </div>
                <div class="text-fg-muted dark:text-fg-muted-dark">{{ l.lesson_title }}</div>
                <div v-if="l.status === 'not reported'" class="mt-1">
                  <span class="badge bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Missing attendance</span>
                </div>
                <div v-else class="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span
                    v-for="(a, ai) in l.attendance"
                    :key="ai"
                    class="inline-flex items-center gap-1 text-fg-secondary dark:text-fg-secondary-dark"
                  >
                    <span class="inline-block w-2 h-2 rounded-full flex-shrink-0" :class="statusDot(a.status)"></span>
                    {{ a.name }} <span class="text-fg-faint dark:text-fg-faint-dark">({{ statusLabel(a.status) }})</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <button v-if="canLoadMore" class="w-full py-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50" :disabled="loadingMore" @click="loadMore">
        {{ loadingMore ? 'Loading…' : 'Show older weeks' }}
      </button>
    </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ExclamationTriangleIcon, ChevronRightIcon, CheckCircleIcon, ClipboardDocumentCheckIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'
import { useAdminSidebar, ATTENDANCE } from '../../composables/useAdminSidebar.js'

const props = defineProps({
  maxHeight: { type: String, default: '250px' },
  contained: { type: Boolean, default: false },
  cards: { type: Boolean, default: false },  // always cards (the workspace sidebar)
})

const { fetchApi } = useApi()

// ── Card-view breakpoint, measured off this tab's own pane width ──────────────
// The tab lives in a user-resizable pane (App.vue's leftColWidth), so a viewport-based
// Tailwind breakpoint (md:) doesn't catch a narrow pane on an otherwise-wide desktop
// window — measure the pane directly instead.
const INVOICES_CARD_BREAKPOINT = 680
const rootEl = ref(null)
const containerWidth = ref(0)
const isNarrow = computed(() => props.cards || (containerWidth.value > 0 && containerWidth.value < INVOICES_CARD_BREAKPOINT))
let containerResizeObserver = null

const isLoading = ref(false)
const error = ref(null)
const weeks = ref([])
const autoPay = ref(false)
const ytdPaid = ref(0)
const year = ref(new Date().getFullYear())
// History: 8 weeks, then 26, then 52 (the API's max). The API stops at the
// school's first weekly invoice, and empty weeks before the teacher's first
// lesson are dropped; either way, fewer weeks back means there's nothing older.
const HISTORY_STEPS = [8, 26, 52]
const weeksLoaded = ref(HISTORY_STEPS[0])
const reachedStart = ref(false)
const loadingMore = ref(false)
const canLoadMore = computed(() => !reachedStart.value && weeksLoaded.value < HISTORY_STEPS.at(-1))
const payrollExcluded = ref(false)

const showGroupPay = computed(() => weeks.value.some(w => w.group_lesson_count > 0))
const hasBothPayTypes = (w) => w.regular_pay > 0 && w.group_lesson_count > 0

const confirming = ref(new Set())
const confirmError = ref({})

const confirmInvoice = async (w) => {
  if (confirming.value.has(w.week_start)) return
  if (!window.confirm(`Confirm your invoice of ${fmt(w.total_due)} for ${rangeLabel(w)}?`)) return

  confirming.value = new Set(confirming.value).add(w.week_start)
  confirmError.value = { ...confirmError.value, [w.week_start]: null }
  try {
    await fetchApi(`/api/lessons/stats/weekly/${w.week_start}/confirm`, { method: 'POST' })
    w.payment_status = 'confirmed'
  } catch (err) {
    confirmError.value = { ...confirmError.value, [w.week_start]: err.message || 'Failed to confirm' }
  } finally {
    const next = new Set(confirming.value)
    next.delete(w.week_start)
    confirming.value = next
  }
}

const expanded = ref(new Set())
const lessonsByWeek = ref({})
const lessonsLoading = ref({})

const fetchWeeks = async (n) => {
  const res = await fetch(`/api/lessons/stats/weekly?weeks=${n}`)
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const data = await res.json()
  const all = data.weeks || []
  let keep = all.length
  while (keep > 0 && !all[keep - 1].lesson_count && !all[keep - 1].payment_status) keep--
  weeks.value = all.slice(0, keep)
  reachedStart.value = keep < n  // fewer back = the start of the teacher's (or school's) history
  weeksLoaded.value = n
  autoPay.value = !!data.auto_pay
  payrollExcluded.value = !!data.payroll_excluded
  ytdPaid.value = data.ytd_paid || 0
  year.value = data.year || year.value
}

const load = async () => {
  if (isLoading.value) return
  isLoading.value = true
  error.value = null
  try {
    await fetchWeeks(weeksLoaded.value)
  } catch (err) {
    error.value = err.message
  } finally {
    isLoading.value = false
  }
}

const loadMore = async () => {
  if (loadingMore.value) return
  loadingMore.value = true
  try {
    await fetchWeeks(HISTORY_STEPS.find(n => n > weeksLoaded.value))
  } catch (err) {
    error.value = err.message
  } finally {
    loadingMore.value = false
  }
}

// Card view groups weeks by the month they start in, each with its total.
const months = computed(() => {
  const out = []
  for (const w of weeks.value) {
    const key = w.week_start.slice(0, 7)
    if (out.at(-1)?.key !== key) {
      const [y, m] = key.split('-').map(Number)
      out.push({ key, label: new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }), weeks: [], total: 0 })
    }
    out.at(-1).weeks.push(w)
    out.at(-1).total += w.total_due
  }
  return out
})

const toggleExpand = async (weekStart) => {
  const next = new Set(expanded.value)
  if (next.has(weekStart)) {
    next.delete(weekStart)
    expanded.value = next
    return
  }
  next.add(weekStart)
  expanded.value = next

  if (lessonsByWeek.value[weekStart]) return
  lessonsLoading.value = { ...lessonsLoading.value, [weekStart]: true }
  try {
    const data = await fetchApi(`/api/lessons/stats/weekly/${weekStart}/lessons`)
    lessonsByWeek.value = { ...lessonsByWeek.value, [weekStart]: data.lessons || [] }
  } catch {
    lessonsByWeek.value = { ...lessonsByWeek.value, [weekStart]: [] }
  } finally {
    lessonsLoading.value = { ...lessonsLoading.value, [weekStart]: false }
  }
}

const fmt = (n) => Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
const fmtSigned = (n) => n < 0 ? `−${fmt(-n)}` : fmt(n)
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''
const fmtLessonDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

const rangeLabel = (w) => {
  const fmtD = (iso) => {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return `${fmtD(w.week_start)} – ${fmtD(w.week_end)}`
}

const totalHours = (w) => {
  const catHours = Object.values(w.category_breakdown || {}).reduce((s, c) => s + c.hours, 0)
  return (w.regular_hours + catHours + (w.group_hours || 0)).toFixed(2)
}

// A true-up correction paid on a later week's invoice counts toward this week.
const paymentDiff = (w) => {
  if (w.payment_status !== 'paid' || w.paid_amount === null) return 0
  return Math.round((w.total_due - w.paid_amount - (w.settled_elsewhere?.amount || 0)) * 100) / 100
}
const settledLabel = (w) => w.settled_elsewhere.with
  ? `Correction paid with week of ${fmtDate(w.settled_elsewhere.with + 'T12:00')}`
  : 'Correction paid separately'

const rowClass = (w) => {
  if (w.payment_status === 'paid')                 return 'bg-green-50/40 dark:bg-green-900/10 hover:bg-green-50 dark:hover:bg-green-900/20'
  if (w.payment_status === 'confirmed')            return 'bg-blue-50/40 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20'
  if (w.payment_status === 'pending_confirmation') return 'bg-blue-50/20 dark:bg-blue-900/5 hover:bg-blue-50/40 dark:hover:bg-blue-900/10'
  if (w.payment_status === 'correction_requested') return 'bg-amber-50/60 dark:bg-amber-900/10 hover:bg-amber-100/60 dark:hover:bg-amber-900/20'
  if (w.not_reported_lessons > 0 && w.total_due > 0) return 'bg-amber-50/60 dark:bg-amber-900/10 hover:bg-amber-100/60 dark:hover:bg-amber-900/20'
  if (w.uncleared_lessons > 0 && w.total_due > 0)    return 'bg-yellow-50/40 dark:bg-yellow-900/10 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
  return 'hover:bg-surface-muted dark:hover:bg-surface-muted-dark'
}

// ── When things happen (Eastern) — mirrors scheduler.py ──────────────────────
// Invoices go out at the 9:30 AM run (once a day, never for payroll-excluded teachers) once the week is over and its attendance is
// done (6-week lookback); auto-confirm is 5 business days later; confirmed weeks
// pay at 10:05 AM / 4:00 PM once every lesson is past its 72h dispute window.
const ET = 'America/New_York'
const INVOICE_RUN = 9 * 60 + 30
const LAST_PAY_RUN = 16 * 60
const INVOICE_LOOKBACK_DAYS = 41
const etDay = (d) => d.toLocaleDateString('en-CA', { timeZone: ET })
const etMinutes = (d) => {
  const [h, m] = d.toLocaleTimeString('en-US', { timeZone: ET, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }).split(':').map(Number)
  return h * 60 + m
}
const addDays = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}
// The day of the first run at or after `from`, given that day's last run time.
const runDay = (from, lastRun) => etMinutes(from) <= lastRun ? etDay(from) : addDays(etDay(from), 1)
const dayLabel = (iso) => {
  const today = etDay(new Date())
  if (iso === today) return 'today'
  if (iso === addDays(today, 1)) return 'tomorrow'
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}
// When a confirmed (or about-to-be-confirmed) week's payment goes out.
const payDay = (w) => {
  const now = new Date()
  const clears = w.clears_at ? new Date(w.clears_at) : null
  return runDay(clears && clears > now ? clears : now, LAST_PAY_RUN)
}
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

const TONE = {
  green:   { textClass: 'text-green-700 dark:text-green-400', pillClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  blue:    { textClass: 'text-blue-700 dark:text-blue-400',   pillClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  amber:   { textClass: 'text-amber-600 dark:text-amber-400', pillClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  neutral: { textClass: 'text-fg-faint dark:text-fg-faint-dark', pillClass: 'bg-surface-muted text-fg-muted dark:bg-surface-raised-dark dark:text-fg-muted-dark' },
}
const AMBER_SUB = 'text-amber-600 dark:text-amber-400'

// { text, sub?, subClass?, textClass, pillClass, action?: 'confirm' | 'attendance' }
const statusInfo = (w) => {
  const st = (tone, text, extra = {}) => ({ ...TONE[tone], text, ...extra })
  const today = etDay(new Date())
  const diff = paymentDiff(w)
  switch (w.payment_status) {
    case 'paid':
      if (diff > 0) return st('green', `Paid ${fmtDate(w.paid_at)}`, { sub: `Underpaid by ${fmt(diff)}`, subClass: AMBER_SUB })
      if (diff < 0) return st('green', `Paid ${fmtDate(w.paid_at)}`, { sub: `Overpaid ${fmt(-diff)}`, subClass: AMBER_SUB })
      if (w.settled_elsewhere) return st('green', `Paid ${fmtDate(w.paid_at)}`, { sub: settledLabel(w) })
      return st('green', `Paid ${fmtDate(w.paid_at)}`)
    case 'confirmed':
      if (!autoPay.value) return st('blue', 'Confirmed', { sub: 'the school pays this directly' })
      return st('blue', `Paying ${dayLabel(payDay(w))}`, { sub: w.uncleared_lessons > 0 ? `after ${plural(w.uncleared_lessons, 'lesson')} clear` : 'Confirmed' })
    case 'pending_confirmation':
      return st('blue', 'Confirm to get paid', { sub: `auto-confirms ${fmtDate(w.auto_confirm_at)}`, action: 'confirm' })
    case 'correction_requested':
      return st('amber', 'Being corrected', { sub: 'a new invoice is coming' })
  }
  if (payrollExcluded.value) return st('neutral', 'Not on payroll')
  if (w.week_end >= today) {
    return st('neutral', 'In progress', { sub: `invoice ${dayLabel(addDays(w.week_end, 1))}` })
  }
  if (w.not_reported_lessons > 0) {
    return st('amber', 'Needs attendance', { sub: `${plural(w.not_reported_lessons, 'lesson')} unmarked`, subClass: AMBER_SUB, action: 'attendance' })
  }
  if (w.total_due === 0) return st('neutral', w.lesson_count ? 'Nothing due' : 'No lessons')
  if (addDays(w.week_end, INVOICE_LOOKBACK_DAYS) < today) return st('amber', 'Invoice delayed', { sub: 'ask the office' })
  return st('neutral', `Invoice ${dayLabel(runDay(new Date(), INVOICE_RUN))}`)
}

// ── Summary card: the next payout, this week so far, and anything to do ───────
const summary = computed(() => {
  const today = etDay(new Date())
  const current = weeks.value.find(w => w.week_end >= today)
  const paid = weeks.value.filter(w => w.payment_status === 'paid').sort((a, b) => (b.paid_at || '').localeCompare(a.paid_at || ''))[0]
  const confirmed = autoPay.value ? weeks.value.filter(w => w.payment_status === 'confirmed' && w.total_due > 0) : []
  const nextDay = confirmed.map(payDay).sort()[0]
  const next = nextDay ? confirmed.filter(w => payDay(w) === nextDay) : []
  return {
    current,
    lastPaid: paid,
    nextDay,
    nextAmount: next.reduce((s, w) => s + w.total_due, 0),
    nextWeeks: next,
    toConfirm: weeks.value.filter(w => w.payment_status === 'pending_confirmation').reverse(),
    unmarked: weeks.value.filter(w => statusInfo(w).action === 'attendance').reduce((s, w) => s + w.not_reported_lessons, 0),
  }
})

// Attendance auto-scrolls to the oldest unmarked lesson when it opens: the
// Dashboard sidebar's tab when it's there, else the mobile Attendance page.
const sidebar = useAdminSidebar()
const router = useRouter()
const goMarkAttendance = () => {
  if (sidebar.host.value === 'dashboard') sidebar.activate(ATTENDANCE)
  else router.push('/')
}

const cardBorderClass = (w) => {
  if (w.payment_status === 'paid')                   return 'border-l-4 border-l-green-500 dark:border-l-green-500'
  if (w.payment_status === 'confirmed')              return 'border-l-4 border-l-blue-400 dark:border-l-blue-500'
  if (w.payment_status === 'pending_confirmation')   return 'border-l-4 border-l-blue-200 dark:border-l-blue-800'
  if (w.payment_status === 'correction_requested')   return 'border-l-4 border-l-amber-400 dark:border-l-amber-500'
  if (w.not_reported_lessons > 0 && w.total_due > 0) return 'border-l-4 border-l-amber-400 dark:border-l-amber-500'
  if (w.uncleared_lessons > 0 && w.total_due > 0)    return 'border-l-4 border-l-yellow-400 dark:border-l-yellow-600'
  return ''
}

const statusDot = (status) => ({
  attended: 'bg-green-500',
  excused:  'bg-yellow-400',
  absent:   'bg-red-500',
}[status] || 'bg-stroke-strong dark:bg-stroke-strong-dark')

const statusLabel = (status) => ({
  attended: 'Attended',
  excused:  'Excused',
  absent:   'Absent',
}[status] || status)

onMounted(() => {
  load()
  if (rootEl.value) {
    containerResizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) containerWidth.value = entry.contentRect.width
    })
    containerResizeObserver.observe(rootEl.value)
  }
})

onUnmounted(() => {
  containerResizeObserver?.disconnect()
})
</script>
